import { useState, useEffect, useRef } from 'react';
import { Card, Alert } from 'reactstrap';
import { Row, Col } from 'src/componentes/grid';
import { FormattedMessage, useIntl } from 'react-intl';
import { Filter } from 'lucide-react';
import useAuth from 'src/hooks/useAuth';

import Button from 'src/componentes/button';
import FiltroChip from 'src/componentes/filtro-chip';
import LoadingContainer from 'src/componentes/loading-container';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import ContentManager from 'src/componentes/content-manager';
import Alerta from 'src/componentes/message-box/alert';
import { getConfiguration } from 'src/services/commonRequests';
import { TIPO_FILTRO_DATA } from 'src/utils/constants';
import TabelaMatrizRisco from '../matriz-farol/tabela';
import TabelaMatrizRiscoComScore from '../matriz-farol/tabela_com_score';
import ProcessoListView from '../../processo/view';
import FiltroDrawerMatrizItensImpactados from './FiltroDrawerMatrizItensImpactados';
import {
  criarFiltroDefault,
  filtroSchema,
  mesclarCamposCustomizados,
  normalizarFiltro,
  obterDescricaoPeriodo,
  obterLabelsFiltro,
  prepararFiltroParaSalvar,
} from './filtro.utils';

const filtroId = 339;
const TIPO_ITEM_PROCESSO = 5;

function MatrizRiscoFarol({
  openLastFilter = true,
  showToolbar = true,
  filtroDefault,
  id,
  isWidget,
  tipoExibicao,
  showTabela = true,
  showMatriz = true,
  columns = [],
  exibirCodigoItem = false,
}) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [filtroDefaultCurrent, setFiltroDefaultCurrent] = useState(
    () => filtroDefault || criarFiltroDefault()
  );
  const [configuracao, setConfiguracao] = useState({});
  const [periodos, setPeriodos] = useState([]);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!isWidget);
  const [refreshRiscoList, setRefreshRiscoList] = useState(0);
  const [quadranteSelecionado, setQuadranteSelecionado] = useState(null);
  const idsSelecionados = quadranteSelecionado?.ids ?? [];
  const [refreshColumns, setRefreshColumns] = useState(0);
  const [positionMouse, setPositionMouse] = useState(-1);
  const [positionInerente, setPositionInerente] = useState(false);
  const [matrizFarol, setMatrizFarol] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsLoadingRisco] = useState(true);
  const prevColumnsRef = useRef();
  const requestVersionRef = useRef(0);
  const camposCustomizadosRef = useRef([]);
  const filtroDefaultString = JSON.stringify(filtroDefault);
  const labels = obterLabelsFiltro(intl, resources);

  useEffect(() => {
    const previous = prevColumnsRef.current;
    const changed =
      !previous ||
      previous.length !== columns.length ||
      previous.some(
        (column, index) => column?.valueField !== columns[index]?.valueField
      );
    if (changed) {
      setRefreshColumns((prev) => prev + 1);
      prevColumnsRef.current = columns;
    }
  }, [columns]);

  useEffect(() => {
    getConfiguration('ItemConfiguracaoRisco')(setConfiguracao)().catch(
      errorHandler
    );
  }, []);

  const exibirAlertaMatrizObrigatoria = () => {
    if (isWidget) return;
    ContentManager.addContent(
      <Alerta
        title={<FormattedMessage id="erro" />}
        message={<FormattedMessage id="label.necessarioSelecionarUmaMatriz" />}
      />
    );
  };

  const refresh = async (filtro) => {
    if (!filtro?.matrizRisco?.id) {
      exibirAlertaMatrizObrigatoria();
      return false;
    }

    const version = ++requestVersionRef.current;
    setIsLoading(true);

    try {
      const response = await http.post(
        '/MatrizItensImpactados/ObterMatrizFarol',
        { filtro }
      );
      if (version !== requestVersionRef.current) return false;
      setFiltroDefaultCurrent(filtro);
      setMatrizFarol(response.data.matrizFarol);
      setQuadranteSelecionado(null);
      setRefreshRiscoList((prev) => prev + 1);
      return true;
    } catch (error) {
      if (version === requestVersionRef.current) errorHandler(error);
      return false;
    } finally {
      if (version === requestVersionRef.current) setIsLoading(false);
    }
  };

  // Carrega o último filtro salvo, os tipos de período e os campos customizados de Itens Impactados antes da primeira busca.
  useEffect(() => {
    if (isWidget) return;
    let cancelled = false;

    const initialize = async () => {
      setIsInitializing(true);
      try {
        const [saved, periodResponse, customResponse] = await Promise.all([
          openLastFilter && !id
            ? http
                .post(`/FiltroColaborador/${filtroId}/AbrirUltimoFiltro`, {})
                .catch(() => null)
            : Promise.resolve(null),
          http.post('/TipoFiltroDataLookup/Search', {
            filter: {
              idsIgnorados: [TIPO_FILTRO_DATA.DESDE_QUANDO_HA_VALORES],
            },
          }),
          http.post('/CampoCustomizado/ObterListaCamposCustomizados', {
            tipoItem: TIPO_ITEM_PROCESSO,
          }),
        ]);
        let filtro = saved?.data?.valor
          ? { ...saved.data.valor, id: saved.data.id }
          : filtroDefault || criarFiltroDefault();

        if (!filtro.matrizRisco?.id) {
          const response = await http
            .post('/MatrizRisco/ObterPrimeiraMatriz', {})
            .catch(() => null);
          const primeiraMatriz = response?.data;
          filtro = {
            ...filtro,
            matrizRisco: primeiraMatriz?.id ? primeiraMatriz : null,
          };
        }
        if (cancelled) return;

        const options = periodResponse.data.list || [];
        camposCustomizadosRef.current = customResponse.data || [];
        filtro = {
          ...normalizarFiltro(filtro, options),
          camposCustomizados: mesclarCamposCustomizados(
            customResponse.data,
            filtro.camposCustomizados
          ),
        };
        setPeriodos(options);
        setFiltroDefaultCurrent(filtro);
        if (filtro.matrizRisco?.id) await refresh(filtro);
      } catch (error) {
        if (!cancelled) errorHandler(error);
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    };

    initialize();
    return () => {
      cancelled = true;
      requestVersionRef.current += 1;
    };
  }, [id, openLastFilter, isWidget]);

  // Widget: o filtro vem pronto da configuração do widget
  useEffect(() => {
    if (!isWidget) return;
    setFiltroDefaultCurrent(filtroDefault || criarFiltroDefault());
    if (filtroDefault?.matrizRisco?.id) {
      refresh(filtroDefault);
    } else {
      setMatrizFarol(undefined);
      setQuadranteSelecionado(null);
      setIsLoading(false);
    }
    return () => {
      requestVersionRef.current += 1;
    };
  }, [isWidget, filtroDefaultString]);

  const handleSearch = async (filtro) => {
    const newFilter = {
      ...normalizarFiltro(filtro, periodos),
      camposCustomizados: mesclarCamposCustomizados(
        camposCustomizadosRef.current.length
          ? camposCustomizadosRef.current
          : filtroDefaultCurrent.camposCustomizados,
        filtro.camposCustomizados
      ),
    };
    if (!(await refresh(newFilter))) return false;
    if (openLastFilter) {
      try {
        await http.post(`/FiltroColaborador/${filtroId}/SalvarFiltro`, {
          filtroId,
          valor: prepararFiltroParaSalvar(newFilter),
        });
      } catch (error) {
        errorHandler(error);
      }
    }
    return true;
  };

  const isCustomFieldSelected = (campo) =>
    campo.campo?.valores?.length > 0 ||
    (Array.isArray(campo.valor)
      ? campo.valor.length > 0
      : campo.valor != null && campo.valor !== '');

  const getFilterChipLabel = (field, value) => {
    if (field === 'camposCustomizados') {
      const campo = value.campo;
      const descricao = campo.valores?.length
        ? campo.valores.map((item) => item.descricao || item.nome).join('; ')
        : campo.opcoesTabela?.find((item) => item.id == value.valor)
            ?.descricao ?? value.valor;
      return campo.nome + ': ' + descricao;
    }
    if (!labels[field]) return;
    if (value === true || value === 'true') return labels[field];
    if (field === 'tipoFiltroData') {
      const periodo = obterDescricaoPeriodo(filtroDefaultCurrent);
      return periodo ? labels[field] + ': ' + periodo : undefined;
    }
    if (typeof value === 'number') return labels[field] + ': ' + value;
    const description = Array.isArray(value)
      ? value
          .map((item) => item.nome || item.descricao || item.legenda)
          .filter(Boolean)
          .join('; ')
      : value?.nome || value?.descricao || value?.legenda;
    return description ? labels[field] + ': ' + description : undefined;
  };

  const setFilter = (ids, severidade, probabilidade, inerente) => {
    const severidadeId = severidade.severidade?.id ?? severidade.id;
    const probabilidadeId = probabilidade.probabilidade?.id ?? probabilidade.id;
    const chave = severidadeId + '-' + probabilidadeId;

    setQuadranteSelecionado((anterior) => {
      const mesmoQuadrante =
        anterior?.inerente === !!inerente && anterior?.chave === chave;

      return ids.length === 0 || mesmoQuadrante
        ? null
        : { ids: [...new Set(ids)], chave, inerente: !!inerente };
    });
    setRefreshRiscoList((prev) => prev + 1);
  };

  const obterMatrizFarol = (inerente) => {
    if (!quadranteSelecionado || quadranteSelecionado.inerente === inerente)
      return matrizFarol;

    return {
      ...matrizFarol,
      riscoAvaliacoes: (matrizFarol?.riscoAvaliacoes ?? []).filter(
        (avaliacao) => idsSelecionados.includes(avaliacao.risco.id)
      ),
    };
  };

  const renderMatrizRiscoScore = (matrizFarol) => {
    return (
      <Row>
        <Col md={12}>
          <Card>
            <TabelaMatrizRiscoComScore
              matrizFarol={matrizFarol}
              riscoAvaliacoesOriginal={matrizFarol.riscoAvaliacoes}
              idsSelecionados={idsSelecionados}
              setPositionMouse={setPositionMouse}
              positionMouse={positionMouse}
              positionInerente={positionInerente}
              setFilter={setFilter}
              isFiltroMatrizRisco={true}
              filtroMatrizRisco={filtroDefaultCurrent}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const setPositions = (mouse, inerente) => {
    setPositionMouse(mouse);
    setPositionInerente(inerente);
  };

  const renderMatrizRiscoScoreComInerente = (matrizFarol) => {
    return (
      <Row>
        {(!tipoExibicao || tipoExibicao.id == 3 || tipoExibicao.id == 1) && (
          <Col md={tipoExibicao && tipoExibicao.id != 1 ? 12 : 6}>
            <Card>
              <TabelaMatrizRiscoComScore
                matrizFarol={obterMatrizFarol(true)}
                riscoAvaliacoesOriginal={matrizFarol.riscoAvaliacoes}
                inerente={true}
                idsSelecionados={idsSelecionados}
                setPositionMouse={setPositions}
                positionMouse={positionMouse}
                positionInerente={positionInerente}
                header={intl.formatMessage(
                  { id: 'processosInerentes' },
                  { processos: resources.processos }
                )}
                setFilter={setFilter}
                isFiltroMatrizRisco={true}
                filtroMatrizRisco={filtroDefaultCurrent}
                exibirCodigoItem={exibirCodigoItem}
              />
            </Card>
          </Col>
        )}
        {(!tipoExibicao || tipoExibicao.id == 2 || tipoExibicao.id == 1) && (
          <Col md={tipoExibicao && tipoExibicao.id != 1 ? 12 : 6}>
            <Card>
              <TabelaMatrizRiscoComScore
                inerente={false}
                idsSelecionados={idsSelecionados}
                matrizFarol={obterMatrizFarol(false)}
                riscoAvaliacoesOriginal={matrizFarol.riscoAvaliacoes}
                setPositionMouse={setPositions}
                positionMouse={positionMouse}
                positionInerente={positionInerente}
                header={intl.formatMessage(
                  { id: 'processosResiduais' },
                  { processos: resources.processos }
                )}
                setFilter={setFilter}
                filtroMatrizRisco={filtroDefaultCurrent}
                exibirCodigoItem={exibirCodigoItem}
              />
            </Card>
          </Col>
        )}
      </Row>
    );
  };

  const { matrizRisco } = filtroDefaultCurrent;
  const { habilitarCalculoDeScores, avaliarRiscoInerente } = configuracao;

  return (
    <LoadingContainer isLoading={isInitializing} onlyLoading>
      {showToolbar && (
        <div className="tw:mb-4 tw:flex tw:items-center tw:gap-4">
          <div className="tw:min-w-0 tw:flex-1">
            <FiltroChip
              filtroId={filtroId}
              onRemoveSaveFilter={false}
              onSearch={handleSearch}
              searchSchema={filtroSchema}
              selectedFilter={filtroDefaultCurrent}
              currentFilter={filtroDefaultCurrent}
              getFilterChipLabel={getFilterChipLabel}
              isCustomFieldSelected={isCustomFieldSelected}
              preventRemoveFilterChip={(field) => field === 'matrizRisco'}
              adjustFilterOnRemoveChip={(_, filtro) =>
                normalizarFiltro(filtro, periodos, matrizRisco)
              }
            />
          </div>
          <Button
            onClick={() => setShowFilterDrawer(true)}
            disabled={isLoading}
            aria-label={intl.formatMessage({ id: 'filtros' })}
          >
            <Filter />
            {intl.formatMessage({ id: 'filtros' })}
          </Button>
          {showFilterDrawer && (
            <FiltroDrawerMatrizItensImpactados
              filtro={filtroDefaultCurrent}
              configuracao={configuracao}
              periodos={periodos}
              onSearch={handleSearch}
              onClose={() => setShowFilterDrawer(false)}
            />
          )}
        </div>
      )}
      {showMatriz && (
        <LoadingContainer isLoading={isLoading}>
          {matrizFarol && habilitarCalculoDeScores ? (
            avaliarRiscoInerente ? (
              renderMatrizRiscoScoreComInerente(matrizFarol)
            ) : (
              renderMatrizRiscoScore(matrizFarol)
            )
          ) : matrizFarol != null &&
            matrizFarol.severidades &&
            matrizFarol.severidades.length > 0 ? (
            <Row>
              {avaliarRiscoInerente &&
                (!tipoExibicao ||
                  tipoExibicao.id == 3 ||
                  tipoExibicao.id == 1) && (
                  <Col md={tipoExibicao && tipoExibicao.id != 1 ? 12 : 6}>
                    <Card>
                      <TabelaMatrizRisco
                        header={intl.formatMessage(
                          { id: 'processosInerentes' },
                          { processos: resources.processos }
                        )}
                        idsSelecionados={idsSelecionados}
                        matrizFarol={obterMatrizFarol(true)}
                        riscoAvaliacoesOriginal={matrizFarol.riscoAvaliacoes}
                        matrizRiscoFarolId={matrizRisco?.id}
                        editMode="page"
                        inerente={true}
                        setPositionMouse={setPositions}
                        positionMouse={positionMouse}
                        positionInerente={positionInerente}
                        setFilter={setFilter}
                        exibirCodigoItem={exibirCodigoItem}
                      />
                    </Card>
                  </Col>
                )}
              {(!tipoExibicao ||
                tipoExibicao.id == 2 ||
                tipoExibicao.id == 1) && (
                <Col
                  md={
                    !avaliarRiscoInerente ||
                    (tipoExibicao && tipoExibicao.id != 1)
                      ? 12
                      : 6
                  }
                >
                  <Card>
                    <TabelaMatrizRisco
                      header={intl.formatMessage(
                        { id: 'processosResiduais' },
                        { processos: resources.processos }
                      )}
                      idsSelecionados={idsSelecionados}
                      matrizFarol={obterMatrizFarol(false)}
                      riscoAvaliacoesOriginal={matrizFarol.riscoAvaliacoes}
                      matrizRiscoFarolId={matrizRisco?.id}
                      editMode="page"
                      inerente={false}
                      setPositionMouse={setPositions}
                      positionMouse={positionMouse}
                      positionInerente={positionInerente}
                      setFilter={setFilter}
                      exibirCodigoItem={exibirCodigoItem}
                    />
                  </Card>
                </Col>
              )}
            </Row>
          ) : (
            <Alert color="light">
              {intl.formatMessage({ id: 'label.nenhumRegistroEncontrado' })}
            </Alert>
          )}
        </LoadingContainer>
      )}
      {matrizFarol && matrizRisco && showTabela && (
        <ProcessoListView
          hideHead={true}
          showNew={false}
          saved={refreshRiscoList}
          hideFiltroChip={true}
          showSearch={false}
          usoMatrizRisco={true}
          filtroId={0}
          ignoreTag={isWidget}
          refreshColumns={refreshColumns}
          showColumns={isWidget ? columns?.map((c) => c.valueField) : null}
          customAction="ObterRiscosMatrizItensImpactados"
          filtroDefault={filtroDefaultCurrent}
          params={{
            idsSelecionados: idsSelecionados,
            isFiltroMatrizRisco: true,
            filtroMatrizRisco: filtroDefaultCurrent,
            colunasUtilizadas: isWidget ? columns : null,
          }}
          usarFiltroSalvo={false}
          setIsLoading={setIsLoadingRisco}
          hierarchy={false}
          hiddenConsolidarOption={true}
          openLastFilter={false}
          hideColumnsButton={isWidget}
        />
      )}
    </LoadingContainer>
  );
}

export default MatrizRiscoFarol;
